import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { CanvasPreview } from 'js/src/components/common/ui/CanvasPreview';
import { Spinner } from 'js/src/components/common/ui/Spinner';
import VideoPreview from 'js/src/components/common/video-preview/VideoPreview';
import { selectLuminaryThumbnail } from 'js/src/data/thumbnail-previews';
import {
    getVideoPlanById,
    isVideoPlanFullyLoaded,
    loadVideoPlanOperation,
    selectLuminaryInstanceLoadedForPlan,
    setLuminaryInstanceLoaded,
} from 'js/src/data/video-planner';
import { type RootState, useAppDispatch } from 'js/src/libs/RootState';
import { useLuminaryContext } from 'js/src/luminary';
import { useLuminaryOperations } from 'js/src/luminary/luminary-hooks';

/**
 * This component loads a luminary instance with the provided VideoPlan and displays a video preview.
 */
export const VideoPlanModalPreview = React.memo(
    ({
        startMuted = true,
        autoplay = true,
        planId,
        hasFullyLoadedRecommendations,
    }: {
        startMuted?: boolean;
        autoplay?: boolean;
        planId: string;
        hasFullyLoadedRecommendations: boolean | undefined;
    }) => {
        const videoPlan = useSelector((state: RootState) => getVideoPlanById(state, planId));
        if (!videoPlan) throw new Error('Missing video plan');
        const [isCreatingPreview, setIsCreatingPreview] = React.useState(false);

        const luminary = useLuminaryContext();
        const dispatch = useAppDispatch();

        const previewCanvas = useSelector((state: RootState) =>
            selectLuminaryThumbnail(state, videoPlan.id),
        );

        const isReadyToLoad = isVideoPlanFullyLoaded(videoPlan);
        const luminaryInstanceLoaded = useSelector((state: RootState) =>
            selectLuminaryInstanceLoadedForPlan(state, planId),
        );

        const { loadVideoPlan } = useLuminaryOperations({
            loadVideoPlanOperation,
        });

        useEffect(() => {
            if (!isReadyToLoad) {
                return;
            }
            if (luminaryInstanceLoaded) {
                return;
            }
            const initLuminary = async () => {
                setIsCreatingPreview(true);
                await loadVideoPlan(videoPlan, false);
                await luminary.onAllSlideImagesRendered();
                await luminary.onCanPlay();
                await luminary.startDrawingToCanvas();
                setIsCreatingPreview(false);
                dispatch(setLuminaryInstanceLoaded({ id: planId, loaded: true }));
            };
            void initLuminary();
        }, [
            dispatch,
            isReadyToLoad,
            loadVideoPlan,
            luminary,
            videoPlan,
            planId,
            luminaryInstanceLoaded,
        ]);

        if (!hasFullyLoadedRecommendations || isCreatingPreview) {
            return (
                <div className="w-100 h-100">
                    {previewCanvas && (
                        <CanvasPreview
                            className="h-100 w-100 position-absolute"
                            sourceCanvas={previewCanvas}
                        />
                    )}
                    <div className="position-relative h-100 d-flex align-items-center justify-content-center">
                        <Spinner
                            className={classNames('', {
                                'color-white': previewCanvas !== undefined,
                            })}
                        />
                    </div>
                </div>
            );
        }

        return (
            <VideoPreview
                startMuted={startMuted}
                autoplay={autoplay}
                showMuteIndicator
                enableComments={false}
                isHtmlVideo={false}
            />
        );
    },
);

VideoPlanModalPreview.displayName = 'VideoPlanModalPreview';
